import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useIntakeFormForProduct } from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import { track } from "@/lib/analytics";
import { PageHeader } from "@/components/app-shell";
import { Icon as BrandIcon } from "@/lib/Icon";

import { IntakeWizard } from "./IntakeWizard";
import { HealthieFormRenderer } from "./HealthieFormRenderer";
import {
  IntakeLoadError,
  IntakeModelGate,
} from "./intake-model/IntakeModelGate";

// Route element for /app/intake. Branches on the `?product=` query param:
//   - present  → ProductIntake (pre-charge, product-scoped renewal intake)
//   - absent   → IntakeWizard  (the awaiting-row-driven onboarding/recovery flow)
// Keeping the branch here leaves the heavily-stepped IntakeWizard untouched.
export function AppIntakeRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const products = (params.get("products") ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  const renewalProduct = params.get("product");
  if (products.length)
    return (
      <IntakeModelGate
        scope={{ product_ids: products, stage: "initial" }}
        renderLegacy={() => (
          <SimplePanel
            title="Continue in the shop"
            description="Choose your treatment to complete its intake as part of your purchase."
            cta="Go to shop"
            onContinue={() => navigate("/app/shop")}
          />
        )}
        onCollected={() => (
          <SimplePanel
            title="Health history saved"
            description="Your intake is saved to your account. You can now continue choosing your treatment."
            cta="Continue to shop"
            onContinue={() => navigate("/app/shop")}
          />
        )}
      />
    );
  return renewalProduct ? (
    <IntakeModelGate
      scope={{ product_ids: [renewalProduct], stage: "renewal" }}
      renderLegacy={() => <ProductIntake />}
      onCollected={() => (
        <IntakeSubmitted
          onContinue={() => navigate("/app", { replace: true })}
        />
      )}
    />
  ) : (
    <IntakeWizard />
  );
}

// Pre-charge, product-scoped intake. Entry point: the 5-day-before-renewal
// intake notice (renewal_intake_notice_scheduler) emails/SMSes a durable link to
// `/app/intake?product=<id>` so the patient can refresh their intake BEFORE the
// renewal charges. No awaiting_prescription row exists yet (that's created at
// the charge), so unlike IntakeWizard we resolve the Healthie form straight
// from the product id via /me/intake/form-for-product/:id and render it. The
// formAnswerGroup the patient submits satisfies the renewal park's Hinds-task
// gate when the charge lands days later (same required_intake_form_id mapping).
function ProductIntake() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const productId = params.get("product") ?? "";
  const [done, setDone] = useState(false);

  const { data, isLoading, error, refetch, isFetching } =
    useIntakeFormForProduct(productId);

  // Defensive: the route only mounts this with ?product= present, but guard
  // against an empty value rather than firing a request for "".
  if (!productId) {
    navigate("/app", { replace: true });
    return null;
  }

  if (done) {
    return (
      <IntakeSubmitted onContinue={() => navigate("/app", { replace: true })} />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-10 type-body-md text-ink-muted px-15 py-30">
        <Loader2 size={14} className="animate-spin" /> Loading your intake…
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 404 && error.code === "no_intake_form") {
    return (
      <NothingToComplete onContinue={() => navigate("/app", { replace: true })} />
    );
  }

  // A failed request is not evidence that a clinical requirement is absent.
  if (error || !data) {
    return (
      <IntakeLoadError
        onRetry={() => {
          void refetch();
        }}
        busy={isFetching}
      />
    );
  }

  return (
    <HealthieFormRenderer
      formId={data.healthie_form_id}
      productName={prettyProduct(productId)}
      productId={productId}
      finalStep
      onSubmitted={() => {
        track("renewal_intake_submitted", { product_id: productId });
        setDone(true);
      }}
    />
  );
}

function IntakeSubmitted({ onContinue }: { onContinue: () => void }) {
  return (
    <SimplePanel
      title="Intake updated"
      description="Thanks — we've got your refreshed intake. Your clinician will use it to write your next prescription when your plan renews. Nothing else to do right now."
      cta="Go to dashboard"
      onContinue={onContinue}
    />
  );
}

function NothingToComplete({ onContinue }: { onContinue: () => void }) {
  return (
    <SimplePanel
      title="Nothing to complete"
      description="There's no intake to update for this item right now. If we need anything from you, it'll show up on your dashboard."
      cta="Go to dashboard"
      onContinue={onContinue}
    />
  );
}

function SimplePanel({
  title,
  description,
  cta,
  onContinue,
}: {
  title: string;
  description: string;
  cta: string;
  onContinue: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col">
      <PageHeader title={title} description={description} />
      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-15 py-15 md:px-40 md:py-20">
        <button
          type="button"
          onClick={onContinue}
          className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 max-w-[64rem] mx-auto"
        >
          {cta}
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-8 h-10"
          >
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
      </div>
    </div>
  );
}

// Single-compound peptide ids use an id-safe compound token ("bpc157",
// "motsc"), not the written name. Only "<compound>-<form>-<n>mg-ml-<n>ml"
// matches, so a blend falls through instead of borrowing the label.
const PEPTIDE_SKU_SUFFIX = /^\d+(?:_\d+)?mg-ml-\d+ml$/;
const PEPTIDE_LABELS: ReadonlyArray<[prefix: string, label: string]> = [
  ["bpc157-injection-", "BPC-157"],
  ["tb500-injection-", "TB-500"],
  ["semax-injection-", "Semax"],
  ["semax-nasal-", "Semax Nasal Spray"],
  ["kpv-cream-", "KPV Cream"],
  ["epitalon-injection-", "Epitalon"],
  ["motsc-injection-", "MOTS-c"],
];

// "nad-injection-200mg-5ml" → "NAD Injection". Best-effort display label for
// the intake description line; the form's own Healthie name is the real title.
function prettyProduct(productId: string): string {
  // Tirzepatide SKUs are `tirzepatide-nia-…` / `tirzepatide-rdt-…`; the second
  // segment is a formulation code ("nia"), not a word worth showing. Collapse
  // the whole family to "Tirzepatide" (its monthly check-in is what routes
  // patients here most).
  if (productId.toLowerCase().startsWith("tirzepatide-")) return "Tirzepatide";
  const peptide = PEPTIDE_LABELS.find(
    ([prefix]) =>
      productId.toLowerCase().startsWith(prefix) &&
      PEPTIDE_SKU_SUFFIX.test(productId.toLowerCase().slice(prefix.length)),
  );
  if (peptide) return peptide[1];
  const family = productId.split("-").slice(0, 2).join(" ");
  return family
    .replace(/\bnad\b/i, "NAD")
    .replace(/\bmic\b/i, "MIC")
    .replace(/\bghk\b/i, "GHK")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
